/**
 * Estimate a virtualised continuous layout on one axis. Pages with known
 * intrinsic dimensions are scaled to `crossSize`; the rest use `fallbackMain`.
 */
export function estimateLinearLayout(pages, opts) {
    const { axis, crossSize, fallbackMain, gap, measured } = opts;
    const sizes = [];
    const offsets = [];
    let acc = 0;
    for (let i = 0; i < pages.length; i++) {
        const m = measured?.get(i);
        let size;
        if (typeof m === 'number' && m > 0) {
            size = m;
        }
        else {
            const p = pages[i];
            if (p.width && p.height && crossSize > 0) {
                size = axis === 'y' ? (p.height / p.width) * crossSize : (p.width / p.height) * crossSize;
            }
            else {
                size = fallbackMain;
            }
        }
        sizes.push(size);
        offsets.push(acc);
        acc += size + gap;
    }
    return { sizes, offsets, total: Math.max(0, acc - gap) };
}
/** Vertical convenience wrapper (kept for existing call sites/tests). */
export function estimateVerticalLayout(pages, contentWidth, fallbackHeight, gap, measured) {
    return estimateLinearLayout(pages, {
        axis: 'y',
        crossSize: contentWidth,
        fallbackMain: fallbackHeight,
        gap,
        ...(measured ? { measured } : {}),
    });
}
/** Indices of pages intersecting the viewport, expanded by `overscanPx`. */
export function visibleRange(layout, scroll, viewportMain, overscanPx) {
    const n = layout.offsets.length;
    if (n === 0)
        return { first: 0, last: -1 };
    const lo = scroll - overscanPx;
    const hi = scroll + viewportMain + overscanPx;
    let first = 0;
    while (first < n - 1 && layout.offsets[first + 1] <= lo)
        first++;
    let last = first;
    while (last < n - 1 && layout.offsets[last + 1] < hi)
        last++;
    return { first, last };
}
/** The page whose box contains (or is nearest past) `scroll`. */
export function pageAtOffset(layout, scroll) {
    const n = layout.offsets.length;
    for (let i = 0; i < n; i++) {
        const next = i + 1 < n ? layout.offsets[i + 1] : layout.total;
        if (scroll < next)
            return i;
    }
    return Math.max(0, n - 1);
}
/** Scroll offset that places the leading edge of `pageIndex` at the viewport edge. */
export function scrollForPage(layout, pageIndex) {
    return layout.offsets[Math.min(Math.max(pageIndex, 0), layout.offsets.length - 1)] ?? 0;
}
//# sourceMappingURL=continuous.js.map